import React from "react";
import { motion } from "framer-motion";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "./SEO";

const PrivacyPolicy = () => {
  return (
    <div className="bg-white text-gray-900 font-sans overflow-x-hidden scroll-smooth">
      <SEO
        title="Privacy Policy"
        description="Greenmark Privacy Policy - Learn how we collect, use, store, and protect your personal data in accordance with Singapore's Personal Data Protection Act 2012."
        keywords="Greenmark, Privacy Policy, PDPA 2012, Singapore, Data Protection"
      />
      <Navbar />

      {/* Header Banner */}
      <section className="relative overflow-hidden bg-white text-gray-800 mt-32 md:mt-31 lg:mt-31">
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage: "url('./contactus.avif')",
            backgroundAttachment: "fixed",
          }}
        ></div>
        <div className="absolute inset-0 bg-[#0b9949]/85"></div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center text-white">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold mb-4"
            id="privacy-policy-title"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-lg opacity-90"
          >
            Effective Date: 04 June 2026
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-slate-50/50">
        <div className="max-w-4xl mx-auto px-6 bg-white py-12 px-8 md:px-12 rounded-2xl shadow-sm border border-slate-100">
          <div className="space-y-8 text-gray-700 leading-relaxed text-sm md:text-base">
            
            {/* Intro */}
            <div className="space-y-4">
              <p>
                <strong>Company Name:</strong> Greenmark <br />
                <strong>Website:</strong>{" "}
                <a
                  href="https://greenmark.com.sg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0b9949] hover:underline"
                >
                  https://greenmark.com.sg
                </a>{" "}
                <br />
                <strong>Contact Number:</strong> +65 8014 7799 <br />
                <strong>Address:</strong> Pioneer Centre, #04-44, Singapore
              </p>
              <p>
                Greenmark respects your privacy and is committed to protecting the personal information you
                share with us. This Privacy Policy explains how we collect, use, store, disclose, and protect your
                personal data when you visit our website, submit an enquiry form, complete a Facebook/Meta
                lead form, contact us by phone or WhatsApp, or engage with our training services.
              </p>
              <p>
                This Privacy Policy is prepared in line with Singapore’s Personal Data Protection Act 2012 (PDPA),
                which governs the collection, use, and disclosure of personal data by organisations in
                Singapore.
              </p>
            </div>

            <hr className="border-slate-100" />

            {/* Sections */}
            <div className="space-y-6">
              
              {/* Section 1 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-1">
                  1. Personal Data We May Collect
                </h2>
                <p className="mb-3">
                  We may collect personal data from you when you submit an enquiry, register interest in our
                  training programmes, contact us directly, or interact with our advertisements.
                </p>
                <p className="mb-2 font-semibold text-gray-800">The personal data we may collect includes:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Full name</li>
                  <li>Company name</li>
                  <li>Job title or role</li>
                  <li>Contact number</li>
                  <li>Email address</li>
                  <li>Course or training programme interest</li>
                  <li>Number of workers or participants for training</li>
                  <li>Preferred training schedule or enquiry details</li>
                  <li>Any message or information you voluntarily provide to us</li>
                </ul>
                <p className="mt-3">
                  For Facebook/Meta Instant Forms, we may collect the information that you submit through the
                  form, such as your name, phone number, company name, and training enquiry details.
                </p>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-2">
                  2. How We Collect Personal Data
                </h2>
                <p className="mb-2 font-semibold text-gray-800">We may collect your personal data through:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Website enquiry forms</li>
                  <li>Facebook/Meta lead generation forms</li>
                  <li>WhatsApp messages</li>
                  <li>Phone calls</li>
                  <li>Email enquiries</li>
                  <li>Course registration forms</li>
                  <li>Business meetings or direct communication</li>
                  <li>Social media enquiries</li>
                  <li>Training-related documents and communications</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-3">
                  3. Purpose of Collecting Personal Data
                </h2>
                <p className="mb-2 font-semibold text-gray-800">
                  Greenmark collects and uses your personal data for legitimate business and training-related
                  purposes, including:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>To respond to your enquiry</li>
                  <li>To contact you regarding construction skill training programmes</li>
                  <li>To share course details, fees, schedules, eligibility, and registration information</li>
                  <li>To follow up with individual or company group training enquiries</li>
                  <li>To arrange training consultation or course registration</li>
                  <li>To provide customer support and administrative assistance</li>
                  <li>To send relevant updates about training programmes and offers</li>
                  <li>To maintain internal enquiry and customer records</li>
                  <li>To improve our services, communication, and marketing performance</li>
                  <li>To comply with applicable legal, regulatory, or operational requirements</li>
                </ul>
                <p className="mt-3">
                  For Meta/Facebook lead forms, your information will be used only for enquiry follow-up, course
                  communication, and training-related service support.
                </p>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-4">
                  4. Contact by Phone, WhatsApp, SMS, or Email
                </h2>
                <p className="mb-3">
                  By submitting your enquiry through our website, Facebook/Meta lead form, WhatsApp, phone
                  call, or any other contact method, you agree that Greenmark may contact you using the details
                  provided.
                </p>
                <p className="mb-2 font-semibold text-gray-800">We may contact you by:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Phone call</li>
                  <li>WhatsApp</li>
                  <li>SMS</li>
                  <li>Email</li>
                </ul>
                <p className="mt-3">
                  This contact will be related to your enquiry, course details, group training offers, training
                  schedules, registration support, or other relevant training information.
                </p>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-5">
                  5. Consent
                </h2>
                <p className="mb-2">
                  By submitting your personal data to Greenmark, you consent to our collection, use, and
                  disclosure of your personal data for the purposes stated in this Privacy Policy.
                </p>
                <p>
                  You may withdraw your consent at any time by contacting us. However, please note that
                  withdrawing consent may affect our ability to respond to your enquiry, process your registration,
                  or provide training-related services.
                </p>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-6">
                  6. Disclosure of Personal Data
                </h2>
                <p className="mb-3">Greenmark does not sell your personal data to third parties.</p>
                <p className="mb-2 font-semibold text-gray-800">We may disclose your personal data only when necessary, including to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Our internal staff or authorised representatives</li>
                  <li>Training coordinators or administrative personnel</li>
                  <li>Service providers who support our business operations</li>
                  <li>Technology, website, CRM, communication, or marketing service providers</li>
                  <li>Government authorities, regulators, or legal bodies when required by law</li>
                  <li>Relevant parties necessary to process training enquiries or registrations</li>
                </ul>
                <p className="mt-3">Any disclosure will be limited to the purpose for which the data is required.</p>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-7">
                  7. Data Protection and Security
                </h2>
                <p className="mb-2">
                  Greenmark takes reasonable steps to protect your personal data from unauthorised access,
                  collection, use, disclosure, copying, modification, disposal, or similar risks.
                </p>
                <p>
                  We may use administrative, technical, and operational safeguards to protect the personal data
                  under our control. However, no method of internet transmission or electronic storage is
                  completely secure, and we cannot guarantee absolute security.
                </p>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-8">
                  8. Accuracy of Personal Data
                </h2>
                <p className="mb-2">
                  We rely on the information you provide to us. Please ensure that your personal data is accurate,
                  complete, and updated.
                </p>
                <p>
                  If your contact details or company information changes, you may contact us to update your
                  information.
                </p>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-9">
                  9. Retention of Personal Data
                </h2>
                <p className="mb-2">
                  Greenmark will retain your personal data only for as long as it is necessary to fulfil the purposes
                  for which it was collected, including enquiry follow-up, course registration, customer service,
                  business record-keeping, legal, accounting, or compliance purposes.
                </p>
                <p>
                  When personal data is no longer required, we will take reasonable steps to delete, anonymise,
                  or securely dispose of it.
                </p>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-10">
                  10. Access and Correction
                </h2>
                <p className="mb-2">
                  You may request access to the personal data we hold about you or request correction of
                  inaccurate personal data.
                </p>
                <p className="mb-2">
                  To make an access or correction request, please contact us using the contact details provided in
                  this Privacy Policy.
                </p>
                <p>We may need to verify your identity before processing your request.</p>
              </div>

              {/* Section 11 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-11">
                  11. Withdrawal of Consent
                </h2>
                <p className="mb-2">
                  You may withdraw your consent for Greenmark to use your personal data for enquiry follow-up,
                  marketing communication, or other purposes.
                </p>
                <p className="mb-2">To withdraw consent, please contact us at:</p>
                <p className="bg-slate-50 p-4 rounded-xl border border-slate-100 font-semibold">
                  Phone / WhatsApp: +65 8014 7799
                </p>
                <p className="mt-3">
                  After receiving your request, we will process it within a reasonable time. Please note that we
                  may still retain certain information where required for legal, regulatory, administrative, or
                  legitimate business purposes.
                </p>
              </div>

              {/* Section 12 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-12">
                  12. Use of Website Cookies and Tracking
                </h2>
                <p className="mb-3">
                  Our website may use cookies, analytics tools, pixels, or similar technologies to understand
                  website performance, improve user experience, and measure marketing effectiveness.
                </p>
                <p className="mb-2 font-semibold text-gray-800">These tools may collect information such as:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Website pages visited</li>
                  <li>Device and browser information</li>
                  <li>General location information</li>
                  <li>Time spent on the website</li>
                  <li>Ad or campaign interaction data</li>
                </ul>
                <p className="mt-3">
                  You may disable cookies through your browser settings. However, some website features may
                  not function properly if cookies are disabled.
                </p>
              </div>

              {/* Section 13 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-13">
                  13. Facebook / Meta Lead Ads
                </h2>
                <p className="mb-2">
                  Greenmark may run advertisements on Facebook, Instagram, or other Meta platforms using
                  Instant Forms.
                </p>
                <p className="mb-2">
                  When you submit your information through a Meta lead form, the information you provide will be
                  shared with Greenmark for the purpose of responding to your enquiry, sharing course details,
                  and providing training-related communication.
                </p>
                <p>
                  Meta may also process your information according to its own data policies. You may refer to
                  Meta’s privacy and data policies through the relevant Meta platform.
                </p>
              </div>

              {/* Section 14 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-14">
                  14. Third-Party Links
                </h2>
                <p className="mb-2">
                  Our website or advertisements may contain links to third-party websites or platforms.
                </p>
                <p className="mb-2">
                  Greenmark is not responsible for the privacy practices, content, or security of third-party
                  websites.
                </p>
                <p>We encourage you to review the privacy policies of any third-party websites you visit.</p>
              </div>

              {/* Section 15 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-15">
                  15. Data Transfer
                </h2>
                <p className="mb-2">
                  Where necessary, your personal data may be stored or processed using service providers,
                  cloud platforms, communication tools, or technology systems that may operate outside
                  Singapore.
                </p>
                <p>
                  Greenmark will take reasonable steps to ensure that your personal data continues to receive a
                  standard of protection consistent with applicable data protection requirements.
                </p>
              </div>

              {/* Section 16 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-16">
                  16. Updates to This Privacy Policy
                </h2>
                <p className="mb-2">
                  Greenmark may update this Privacy Policy from time to time to reflect changes in our services,
                  business practices, legal requirements, or data protection procedures.
                </p>
                <p>The updated version will be published on this page with a revised effective date.</p>
              </div>

              {/* Section 17 */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3" id="sec-17">
                  17. Contact Us
                </h2>
                <p className="mb-3">
                  For any questions about this Privacy Policy, your personal data, access or correction requests,
                  or withdrawal of consent, please contact us:
                </p>
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 space-y-1">
                  <p className="font-bold text-gray-900">Greenmark</p>
                  <p>Pioneer Centre, #04-44, Singapore</p>
                  <p>Phone / WhatsApp: +65 8014 7799</p>
                  <p>
                    Website:{" "}
                    <a
                      href="https://greenmark.com.sg"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0b9949] hover:underline"
                    >
                      https://greenmark.com.sg
                    </a>
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
